import { useEffect, useState } from "react";
import { FiPlus, FiTrash2 } from "react-icons/fi";
import CommonTextEditor from "components/common/CommonTextEditor";
import FileUpload from "components/FileUpload";
import { useProjectFormContext } from "pages/project/context/ProjectFormContext";
import { isFileObject } from "utils/uploadFormItems";

function newMember() {
  return {
    id: `member-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    name: "",
    position: "",
    municipality: "",
    email: "",
    occupation: "",
    description: "",
    imageUrl: "",
    imagePending: "",
  };
}

function itemFromUrl(url) {
  const u = String(url || "").trim();
  if (!u) return [];
  return [{ id: "team-img", file: u, status: "complete", progress: 100 }];
}

function FormField({ label, error, children }) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-xs font-semibold text-text-500">{label}</label>
      {children}
      {error && (
        <p className="text-xs font-medium text-red-600" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

const inputClass = (hasError) =>
  [
    "w-full rounded-lg border px-3 py-2 text-sm text-text-900 placeholder:text-text-400 outline-none transition",
    "focus:ring-2 focus:ring-brand-200 focus:border-brand-400",
    hasError
      ? "border-red-400 ring-1 ring-red-200 bg-red-50/30"
      : "border-slate-200 bg-white shadow-sm hover:border-slate-300",
  ].join(" ");

function memberFieldErrors(errors, member, index) {
  const byId = errors.teamMemberErrorsById?.[member.id];
  if (byId) return byId;
  const byIndex = Array.isArray(errors.teamMemberErrors) ? errors.teamMemberErrors[index] : null;
  return byIndex || {};
}

export default function StepTeamDescription() {
  const { values, setField, errors } = useProjectFormContext();

  const members = Array.isArray(values.teamMembers) && values.teamMembers.length
    ? values.teamMembers
    : [newMember()];

  useEffect(() => {
    if (!Array.isArray(values.teamMembers) || values.teamMembers.length === 0) {
      setField("teamMembers", [newMember()]);
    }
  }, [values.teamMembers, setField]);

  const [imageItemsMap, setImageItemsMap] = useState(() => {
    const map = {};
    members.forEach((m) => { map[m.id] = itemFromUrl(m.imageUrl); });
    return map;
  });

  // Sync map when members are added/removed externally (e.g. edit mode API load)
  useEffect(() => {
    setImageItemsMap((prev) => {
      const currentIds = new Set(members.map((m) => m.id));
      const newEntries = members.filter((m) => !prev[m.id]);
      const removedIds = Object.keys(prev).filter((id) => !currentIds.has(id));

      if (!newEntries.length && !removedIds.length) return prev;

      const next = { ...prev };
      newEntries.forEach((m) => { next[m.id] = itemFromUrl(m.imageUrl); });
      removedIds.forEach((id) => { delete next[id]; });
      return next;
    });
  }, [members]);

  const updateMember = (id, patch) => {
    setField("teamMembers", members.map((m) => (m.id === id ? { ...m, ...patch } : m)));
  };

  const addMember = () => {
    const member = newMember();
    setField("teamMembers", [...members, member]);
    setImageItemsMap((prev) => ({ ...prev, [member.id]: [] }));
  };

  const removeMember = (id) => {
    if (members.length <= 1) return;
    setField("teamMembers", members.filter((m) => m.id !== id));
    setImageItemsMap((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const handleImageChange = (memberId, items) => {
    setImageItemsMap((prev) => ({ ...prev, [memberId]: items }));
    const first = items[0];
    if (!first) {
      updateMember(memberId, { imageUrl: "", imagePending: "" });
      return;
    }
    const url =
      typeof first.file === "string"
        ? String(first.file).trim()
        : String(first?.url ?? "").trim();
    if (url) {
      updateMember(memberId, { imageUrl: url, imagePending: "" });
      return;
    }
    if (isFileObject(first.file)) {
      updateMember(memberId, { imagePending: first.file.name, imageUrl: "" });
      return;
    }
    updateMember(memberId, { imageUrl: "", imagePending: "" });
  };

  return (
    <div className="space-y-5 transition-opacity duration-300">
      {errors.teamMembers && (
        <p className="text-xs font-medium text-red-600" role="alert">
          {errors.teamMembers}
        </p>
      )}

      {members.map((member, index) => {
        const me = memberFieldErrors(errors, member, index);
        return (
          <div
            key={member.id}
            className="rounded-xl border border-slate-200 bg-slate-50/50 p-5 shadow-sm space-y-5"
          >
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-semibold text-text-700">
                Team Member {index + 1}
                {member.name && (
                  <span className="ml-2 font-normal text-text-400">— {member.name}</span>
                )}
              </h4>
              {members.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeMember(member.id)}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-semibold text-red-600 transition hover:bg-red-50"
                >
                  <FiTrash2 className="h-3.5 w-3.5" />
                  Remove
                </button>
              )}
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <FormField label="Name" error={me.name}>
                <input
                  type="text"
                  value={member.name}
                  onChange={(e) => updateMember(member.id, { name: e.target.value })}
                  placeholder="e.g. Sarah Chen"
                  className={inputClass(me.name)}
                />
              </FormField>

              <FormField label="Position at Issuer" error={me.position}>
                <input
                  type="text"
                  value={member.position}
                  onChange={(e) => updateMember(member.id, { position: e.target.value })}
                  placeholder="e.g. CEO & Co-founder"
                  className={inputClass(me.position)}
                />
              </FormField>

              <FormField label="Municipality of Residence" error={me.municipality}>
                <input
                  type="text"
                  value={member.municipality}
                  onChange={(e) => updateMember(member.id, { municipality: e.target.value })}
                  placeholder="e.g. Toronto, Ontario"
                  className={inputClass(me.municipality)}
                />
              </FormField>

              <FormField label="Email Address" error={me.email}>
                <input
                  type="email"
                  value={member.email}
                  onChange={(e) => updateMember(member.id, { email: e.target.value })}
                  placeholder="e.g. sarah@company.com"
                  className={inputClass(me.email)}
                />
              </FormField>
            </div>

            <FormField
              label="Principal Occupation for the Last Five Years"
              error={me.occupation}
            >
              <textarea
                rows={3}
                value={member.occupation}
                onChange={(e) => updateMember(member.id, { occupation: e.target.value })}
                placeholder="e.g. CEO at Acme Corp (2021–present); Director at Beta Ltd (2019–2021)…"
                className={inputClass(me.occupation)}
              />
            </FormField>

            <fieldset className="min-w-0 border-0 p-0">
              <legend className="mb-2 block w-full px-0 text-xs font-semibold text-text-500">
                About
              </legend>
              <div
                className={[
                  "overflow-hidden rounded-xl border bg-white",
                  me.description ? "border-red-400 ring-1 ring-red-200" : "border-slate-200 shadow-card",
                ].join(" ")}
              >
                <CommonTextEditor
                  value={member.description}
                  onChange={(html) => updateMember(member.id, { description: html })}
                  placeholder="Introduce this team member…"
                  height={220}
                  error={me.description}
                />
              </div>
              {me.description && (
                <p className="mt-1 text-xs font-medium text-red-600" role="alert">
                  {me.description}
                </p>
              )}
            </fieldset>

            <div>
              {me.imageUrl && (
                <p className="mb-2 text-xs font-medium text-red-600" role="alert">
                  {me.imageUrl}
                </p>
              )}
              <FileUpload
                title="Team member image"
                accept=".jpg,.jpeg,.png,.webp"
                multiple={false}
                maxSizeMB={10}
                helperText="JPG, PNG, or WebP. Max 10 MB."
                items={imageItemsMap[member.id] ?? []}
                onItemsChange={(items) => handleImageChange(member.id, items)}
                showPreviewActions
                showPreviewProgress
              />
            </div>
          </div>
        );
      })}

      <button
        type="button"
        onClick={addMember}
        className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-brand-400 bg-white py-3 text-sm font-semibold text-brand-600 transition hover:bg-brand-50"
      >
        <FiPlus className="h-4 w-4" />
        Add Team Member
      </button>
    </div>
  );
}
